import moment from 'moment';
import type { Theme } from '@mui/material';
import type { WizerConnectorDetailsResponse } from '../../../api/connectors/connectors.api';
import {
  ProductFamilyCode,
  type Contract,
  type PreferenceRecordedOpportunity,
  type ProductFamily,
  type Subscription,
} from '../types';
import { ServiceStatus } from '../types';

export const getServiceStatus = ({
  productFamily,
  wizer,
  subscriptions,
  opportunities,
  contracts,
}: {
  productFamily?: ProductFamily;
  wizer?: WizerConnectorDetailsResponse;
  subscriptions: Subscription[];
  opportunities: PreferenceRecordedOpportunity[];
  contracts: Contract[];
}): ServiceStatus => {
  const isServiceActivated = getIsServiceActivated({
    productFamily,
    wizer,
  });
  const isRequestReceived = getIsRequestReceived({
    opportunities,
    productFamily,
  });
  const isServiceActivationInProgress = getIsActivating({
    productFamily,
    subscriptions,
    contracts,
  });

  if (isServiceActivated) {
    return ServiceStatus.Activated;
  }

  if (isServiceActivationInProgress) {
    if (productFamily?.code === ProductFamilyCode.PTS) {
      return ServiceStatus.TestingInProgress;
    }

    return ServiceStatus.ActivationInProgress;
  }

  if (isRequestReceived) {
    return ServiceStatus.RequestReceived;
  }

  return ServiceStatus.NotActivated;
};

export const getIsServiceActivated = ({
  productFamily,
  wizer,
}: {
  productFamily?: ProductFamily;
  wizer?: WizerConnectorDetailsResponse;
}): boolean => {
  if (
    productFamily?.code === ProductFamilyCode.CTS &&
    wizer?.dashboardMagicLink
  ) {
    return true;
  }

  return false;
};

export const getIsRequestReceived = ({
  opportunities,
  productFamily,
}: {
  opportunities: PreferenceRecordedOpportunity[];
  productFamily?: ProductFamily;
}) => {
  const isRequestReceived = opportunities.some(
    (opportunity) => opportunity.productFamilyId === productFamily?.id
  );

  return isRequestReceived;
};

export const getIsActivating = ({
  productFamily,
  subscriptions = [],
  contracts = [],
}: {
  productFamily?: ProductFamily;
  subscriptions: Subscription[];
  contracts: Contract[];
}) => {
  if (Array.isArray(subscriptions) && subscriptions.length > 0) {
    return subscriptions?.some(
      (subscription) =>
        subscription?.productFamily?.id === productFamily?.id &&
        moment(subscription.endDate).isAfter(moment())
    );
  }

  if (Array.isArray(contracts) && contracts.length > 0) {
    return contracts?.some((contract) => {
      return contract.productFamily?.id === productFamily?.id;
    });
  }

  return false;
};

export const getServiceStatusText = (serviceStatus: ServiceStatus) => {
  switch (serviceStatus) {
    case ServiceStatus.Activated:
      return 'Activated';

    case ServiceStatus.Purchased:
      return 'Purchased';

    case ServiceStatus.TestingCompleted:
      return 'Testing Completed';

    case ServiceStatus.RequestReceived:
      return 'Request Received';

    case ServiceStatus.ActivationInProgress:
      return 'Activation In Progress';

    case ServiceStatus.TestingInProgress:
      return 'Testing In Progress';

    case ServiceStatus.NotActivated:
      return 'Not Activated';

    default:
      return 'Not Activated';
  }
};

export const getServiceStatusColor = (
  serviceStatus: ServiceStatus,
  theme: Theme
) => {
  switch (serviceStatus) {
    case ServiceStatus.Activated:
    case ServiceStatus.Purchased:
    case ServiceStatus.TestingCompleted:
      return theme.palette.common.green1;

    case ServiceStatus.RequestReceived:
    case ServiceStatus.ActivationInProgress:
    case ServiceStatus.TestingInProgress:
      return theme.palette.common.favoriteOrange;

    case ServiceStatus.NotActivated:
      return theme.palette.common.darkGrey3;

    default:
      return theme.palette.common.darkGrey3;
  }
};
